// Customer-display orchestrator (Capacitor Android only). Ported from
// cartadigital-pos app/plugins/customer-display.client.ts.
//
// Push pipeline: payload-signature watch; idle payloads sent immediately (a
// generation counter cancels stale debounced sends); order payloads debounced
// 150 ms; single in-flight setState with one queued slot; acked-signature
// dedupe; retry backoff 500 ms doubling to 15 s while the failed signature is
// still current; show() on start and on visibilitychange → visible.
import { computed, ref, watch } from 'vue';
import { useDebounceFn } from '@vueuse/core';
import {
  CustomerDisplay,
  isCustomerDisplayAvailable,
} from '@/lib/capacitor/CustomerDisplay';
import { buildCustomerDisplayState } from '@/domain/customer-display/build-customer-display-state';
import type {
  CustomerDisplayColorMode,
  ICustomerDisplayPayload,
} from '@/domain/customer-display/customer-display.types';
import { usePosMoney } from '@/composition/use-pos-money';
import { useMenuStore } from '@/composition/use-menu';
import { usePosDraftsStore } from '@/stores/pos-drafts.store';
import { useRestaurantStore } from '@/stores/restaurant.store';
import { Globals } from '@/modules/Globals';
import { getTopLevelDomain } from '@/utils/domain.utils';

export interface IInitCustomerDisplayOptions {
  /** Reactive getter for the current route name (e.g. `() => $route.name`). */
  getRouteName: () => string | null;
  /** Reactive getter for Vuetify's dark flag (`() => $vuetify.theme.dark`). */
  getIsDark: () => boolean;
}

const SEND_DEBOUNCE_MS = 150;
const RETRY_BASE_MS = 500;
const RETRY_MAX_MS = 15_000;

let initialized = false;

/**
 * Starts the customer-display push pipeline. Called once from App.vue's
 * Capacitor init; bails on non-Android platforms. App-lifetime singleton —
 * nothing here is ever torn down.
 */
export function initCustomerDisplay(
  options: IInitCustomerDisplayOptions
): void {
  if (initialized) return;
  if (!isCustomerDisplayAvailable()) return;
  initialized = true;

  const restaurantStore = useRestaurantStore();
  const posDrafts = usePosDraftsStore();
  const menuStore = useMenuStore();
  const posMoney = usePosMoney();

  const platformLabel = getTopLevelDomain();

  const payload = computed<ICustomerDisplayPayload>(() => {
    const colorMode: CustomerDisplayColorMode = options.getIsDark()
      ? 'dark'
      : 'light';

    return buildCustomerDisplayState({
      routeName: options.getRouteName(),
      activeDraft: posDrafts.activeDraft,
      restaurant: restaurantStore.restaurant,
      itemMap: menuStore.itemMap,
      colorMode,
      uploadsPath: Globals.uploadsPath,
      platformLabel,
      formatComputedMoney: posMoney.formatComputedMoney,
    });
  });

  const payloadSignature = computed(() => JSON.stringify(payload.value));

  const lastAckedSignature = ref<string | null>(null);
  const generation = ref(0);

  let inFlight = false;
  let queued: { payload: ICustomerDisplayPayload; signature: string } | null =
    null;
  let retryTimer: ReturnType<typeof setTimeout> | null = null;
  let retryAttempt = 0;
  let retrySignature: string | null = null;

  const clearRetry = (): void => {
    if (retryTimer != null) clearTimeout(retryTimer);
    retryTimer = null;
    retryAttempt = 0;
    retrySignature = null;
  };

  const computeRetryDelayMs = (attempt: number): number => {
    const delay = RETRY_BASE_MS * Math.pow(2, Math.max(0, attempt - 1));
    return Math.min(RETRY_MAX_MS, delay);
  };

  const scheduleRetry = (
    nextPayload: ICustomerDisplayPayload,
    signature: string
  ): void => {
    if (signature !== payloadSignature.value) return;
    if (retryTimer != null) return;

    retryAttempt += 1;
    retrySignature = signature;

    const delayMs = computeRetryDelayMs(retryAttempt);
    retryTimer = setTimeout(() => {
      retryTimer = null;
      void sendInternal(nextPayload, signature);
    }, delayMs);
  };

  const sendInternal = async (
    nextPayload: ICustomerDisplayPayload,
    signature: string
  ): Promise<void> => {
    if (signature === lastAckedSignature.value) return;

    // If a previous signature was failing, but we now want a different one,
    // drop its retries.
    if (retrySignature != null && retrySignature !== signature) {
      clearRetry();
    }

    if (inFlight) {
      queued = { payload: nextPayload, signature };
      return;
    }

    inFlight = true;
    try {
      const res = await CustomerDisplay.setState({ payload: nextPayload });

      // available && !applied means the page wasn't ready to run __cd_apply
      // (still loading, error page being retried natively, …): treat it as a
      // soft failure so the backoff loop re-sends until it actually applies.
      // !available is a success: the native side keeps the last payload and
      // applies it when a display gets attached.
      if (res.available && !res.applied) {
        scheduleRetry(nextPayload, signature);
        return;
      }

      // If this send was a retry, stop retrying once it succeeds.
      if (retrySignature === signature) clearRetry();

      // Only mark as acked if this is still the current desired state.
      if (signature === payloadSignature.value) {
        lastAckedSignature.value = signature;
      }
    } catch (error) {
      console.warn('[customer-display] setState failed', error);
      scheduleRetry(nextPayload, signature);
    } finally {
      inFlight = false;

      const next = queued;
      queued = null;
      if (next != null) {
        void sendInternal(next.payload, next.signature);
      }
    }
  };

  const sendDebounced = useDebounceFn(
    (nextPayload: ICustomerDisplayPayload, signature: string, gen: number) => {
      if (gen !== generation.value) return;
      void sendInternal(nextPayload, signature);
    },
    SEND_DEBOUNCE_MS
  );

  const scheduleSend = (
    nextPayload: ICustomerDisplayPayload,
    signature: string
  ): void => {
    // "Idle" clears immediately so the customer never sees stale items.
    if (nextPayload.mode === 'idle') {
      generation.value += 1;
      void sendInternal(nextPayload, signature);
      return;
    }

    const gen = generation.value;
    void sendDebounced(nextPayload, signature, gen);
  };

  watch(payloadSignature, (signature) => {
    scheduleSend(payload.value, signature);
  });

  // Re-show when returning to the app (some OEMs can dismiss presentations).
  const onVisibilityChange = (): void => {
    if (document.visibilityState !== 'visible') return;

    void CustomerDisplay.show().catch((e) =>
      console.warn('[customer-display] show failed', e)
    );
    scheduleSend(payload.value, payloadSignature.value);
  };
  document.addEventListener('visibilitychange', onVisibilityChange);

  void CustomerDisplay.show().catch((e) =>
    console.warn('[customer-display] show failed', e)
  );
  scheduleSend(payload.value, payloadSignature.value);
}
